import { isFulfilled } from "@reduxjs/toolkit";
import SlIcon from "@shoelace-style/shoelace/dist/react/icon";
import { useEffect, useState } from "react";
import { LiaSpinnerSolid } from "react-icons/lia";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../app/store";
import { useToastError } from "../../hooks/useToast";
import { reserveSectionThunk } from "../../reduxAsyncThunks/transaction.thunks";
import { setConfirmChangeSectionData, setSectionToChange } from "../../reduxSlices/globalFlagsSlice";
import { selectStudent } from "../../reduxSlices/studentSlice";
import {
  selectInscribedSubjects,
  selectSectionChangeInProcess,
  selectSubcriptionProcessSectionIds,
  selectSubjectsQuotas,
} from "../../reduxSlices/subjectsSlice";
import { IOfferedSubjectData, IQuota } from "../../types";
import { getCourseQuota } from "../../utils/quota";
import "./ButtonInscription.css";
import Translatable from "../Translatable/Translatable";

interface BtnProps {
  id: number;
  idMateria: number;
  subjectData: IOfferedSubjectData;
  quota?: IQuota;
}

export default function ButtonInscription({ id, idMateria, subjectData, quota: quotaProp }: BtnProps) {
  const dispatch = useAppDispatch();
  const showError = useToastError();
  /* ------------------------------ global store ------------------------------ */
  const student = useSelector(selectStudent);
  const inscribedSubjects = useSelector(selectInscribedSubjects);
  const subcriptionProcessSectionIds = useSelector(selectSubcriptionProcessSectionIds);
  const subjectsQuotas = useSelector(selectSubjectsQuotas);
  const sectionChangeInProcess = useSelector(selectSectionChangeInProcess);
  /* ------------------------------- local state ------------------------------ */
  const [isSubInProcess, setIsSubInProcess] = useState(false);
  const [isChangingSection, setIsChangingSection] = useState(false);
  /* ------------------------------ calculations ------------------------------ */
  const materiasList = Array.isArray(inscribedSubjects) ? inscribedSubjects.map((s) => s.id) : [];
  const cambioSeccion = materiasList.indexOf(idMateria) > -1;
  const quota = getCourseQuota(subjectsQuotas, subjectData.reserveData.idC);
  const { availability, quotaId } = quota;
  const idCurso = subjectData.reserveData.idS;
  const anioLectivo = subjectData.reserveData.aL;
  /* -------------------------------- Effeccts -------------------------------- */
  useEffect(() => {
    const isProcessing = Array.isArray(subcriptionProcessSectionIds)
      ? subcriptionProcessSectionIds.some((item) => item.idCurso === idCurso && item.anioLectivo === anioLectivo)
      : false;
    if (isProcessing) {
      setIsSubInProcess(true);
    } else {
      setIsSubInProcess(false);
    }
  }, [subcriptionProcessSectionIds]);

  useEffect(() => {
    const isChangingSectionForThisSubject = Array.isArray(sectionChangeInProcess)
      ? sectionChangeInProcess.some((item) => item.idCurso === idCurso && item.anioLectivo === anioLectivo)
      : false;
    setIsChangingSection(isChangingSectionForThisSubject);
  }, [sectionChangeInProcess]);
  /* -------------------------------- handlers -------------------------------- */
  const resetSectionChangeData = () => {
    dispatch(setSectionToChange(null));
    dispatch(setConfirmChangeSectionData(null));
  };

  const handleChangeGlobalData = () => {
    resetSectionChangeData();
    dispatch(
      setSectionToChange({ id: Number(id), idMateria: Number(idMateria), anioLectivo: subjectData.reserveData.aL }),
    );
    dispatch(setConfirmChangeSectionData(subjectData));
  };

  const handleTryReserve = async (idCurso: number) => {
    const result = await dispatch(
      reserveSectionThunk({
        idC: subjectData.reserveData.idC,
        idS: subjectData.reserveData.idS,
        idA: subjectData.reserveData.idA,
        tim: subjectData.reserveData.tim,
        tic: subjectData.reserveData.tic,
        aL: subjectData.reserveData.aL,
        idPersona: student.id.toString(),
        idMateria: subjectData.reserveData.idMateria,
        idCurso: idCurso,
      }),
    );

    if (!isFulfilled(result)) {
      showError(result.payload as string);
    }
  };

  if (!student.prioridad) {
    return null;
  }

  if (availability <= 0 || (typeof quotaProp?.availability === "number" && quotaProp?.availability <= 0)) {
    return (
      <button className="desktop-action-add-noquota" disabled>
        <SlIcon name="x"></SlIcon>
        <Translatable as="span" capitalize>Sin cupo</Translatable>
      </button>
    );
  }

  const buttonDisabled =
    isSubInProcess ||
    (cambioSeccion && Array.isArray(sectionChangeInProcess) && sectionChangeInProcess.length > 0) ||
    quotaId === -1 ||
    subjectData.reserveData.idC === -1 ||
    quotaProp?.quotaId === -1 || quotaProp?.readyToUse === false;

  return (
    <button
      style={{ width: `${cambioSeccion ? "165px" : ""}` }}
      className={`desktop-action-add ${buttonDisabled ? "desktop-action-add-disabled" : ""}`}
      onClick={cambioSeccion ? () => handleChangeGlobalData() : () => handleTryReserve(id)}
      disabled={buttonDisabled}
    >
      {isSubInProcess || isChangingSection ? (
        <>
          <LiaSpinnerSolid className="spinner-animation" />
          <Translatable as="span" capitalize>Procesando</Translatable>
        </>
      ) : (
        <>
          {cambioSeccion ? (
            <>
              <SlIcon name="arrow-left-right"></SlIcon>
              <Translatable as="span" capitalize>Cambiar sección</Translatable>
            </>
          ) : (
            <>
              <SlIcon name="plus"></SlIcon>
              <Translatable as="span" capitalize>Inscribirme</Translatable>
            </>
          )}
        </>
      )}
    </button>
  );
}
